import { Button, Icon, Input, Tooltip } from "@deligoo/ui"
import clsx from "clsx"
import localForage from "localforage"
import { FormEvent, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { shallowEqual } from "react-redux"
import { Instance as TippyInstance } from "tippy.js"

import { useAppDispatch, useAppSelector } from "@/App"
import { setMapLayers } from "@/store/global"
import { setClientsSearchValue } from "@/store/ui"
import { ClientUtils } from "@/utils"

import cls from "./MapLayers.module.scss"

export const CLIENT_MAP_LAYERS = ["gastronomy", "ecommerce", "retail", "groceries", "premium_services"] as const

const BASE_MAP_LAYERS = [
  { value: "online_drivers", icon: "driver-active" },
  { value: "offline_drivers", icon: "driver-inactive" },
  { value: "finished_orders", icon: "tasks-done" },
  { value: "failed_orders", icon: "tasks-closed" },
  { value: "canceled_orders", icon: "close-circle" },
  { value: "traffic", icon: "traffics" },
  { value: "team_delivery_zone", icon: "map-view" },
] as const

export type ClientMapLayer = typeof CLIENT_MAP_LAYERS[number]
export type BaseMapLayer = typeof BASE_MAP_LAYERS[number]["value"]
export type MapLayer = Array<BaseMapLayer | ClientMapLayer>[number]

export const MapLayers = () => {
  const selectedLayers = useAppSelector((state) => state.global.mapLayers, shallowEqual)

  const [isCollapsed, setCollapsed] = useState(true)
  const [tippyInstance, setTippyInstance] = useState<TippyInstance | null>(null)
  const [isSearchBarActive, setSearchBarActive] = useState(false)

  const selectedClientLayers = getSelectedClientLayers(selectedLayers)

  const dispatch = useAppDispatch()

  const handleLayerToggle = (value: MapLayer) => {
    const index = selectedLayers.indexOf(value)
    const layersToSelect = [...selectedLayers]

    if (index === -1) {
      layersToSelect.push(value)
    } else {
      layersToSelect.splice(index, 1)
    }

    dispatch(setMapLayers(layersToSelect))
    localForage.setItem("map_layers", layersToSelect)

    if (value === "traffic" && index === -1) {
      setTimeout(autoOffTraffic, 600000)
    }
  }

  async function autoOffTraffic() {
    const savedLayers = (await localForage.getItem<MapLayer[]>("map_layers")) || []
    const index = savedLayers.indexOf("traffic")

    if (index > -1) {
      savedLayers.splice(index, 1)
      dispatch(setMapLayers(savedLayers))
      localForage.setItem("map_layers", selectedLayers)
    }
  }

  useEffect(() => {
    async function retrieveSavedLayers() {
      const savedLayers = await localForage.getItem<MapLayer[]>("map_layers")

      if (savedLayers) dispatch(setMapLayers(savedLayers))
    }

    retrieveSavedLayers()
  }, [dispatch])

  const { t } = useTranslation()

  return (
    <div className={cls.mapMenu}>
      <div className={clsx(cls.layers, isCollapsed && cls.collapsed)}>
        <div className={clsx(cls.layer, false && cls.selected)}>
          <Tooltip
            interactive
            onCreate={(instance) => setTippyInstance(instance)}
            placement="bottom-start"
            offset={[-24, 32]}
            content={
              <div className={cls.dropdown}>
                <div className={cls.dropdownSearchBtn}>
                  <Button
                    onClick={() => {
                      setSearchBarActive(true)
                      tippyInstance?.hide()
                    }}
                    iconProps={{ name: "search" }}
                    className={cls.dropdownItem}
                    color="muted"
                    variation="contrast"
                    disabled={!selectedClientLayers.length}
                    fullWidth
                  >
                    Wyszukaj
                  </Button>
                </div>

                {CLIENT_MAP_LAYERS.map((clientLayer) => (
                  <Button
                    key={clientLayer}
                    onClick={() => handleLayerToggle(clientLayer)}
                    iconProps={{ name: ClientUtils.getIconForLocationType(clientLayer) }}
                    className={cls.dropdownItem}
                    color={selectedLayers.includes(clientLayer) ? "primary" : "muted"}
                    variation="contrast"
                    fullWidth
                  >
                    {t(`clients:location_types.${clientLayer}`)}
                  </Button>
                ))}
              </div>
            }
          >
            <div>
              <Button
                size="small"
                variation="text"
                className={cls.dropdownTrigger}
                color={selectedClientLayers.length ? "primary" : undefined}
                onClick={() =>
                  selectedClientLayers.length > 0
                    ? selectedClientLayers.forEach((layer) => handleLayerToggle(layer))
                    : handleLayerToggle("gastronomy")
                }
                iconProps={{
                  name:
                    selectedClientLayers.length === 0
                      ? ClientUtils.getIconForLocationType("gastronomy")
                      : selectedClientLayers.length === 1
                      ? ClientUtils.getIconForLocationType(selectedClientLayers[0])
                      : "check-circle",
                  size: selectedClientLayers.length > 1 ? "small" : "medium",
                }}
              >
                {selectedClientLayers.length > 1 && selectedClientLayers.length}
                <Icon name="chevron-tiny-down" size="small" color="muted" className="ml-0.5" />
              </Button>
            </div>
          </Tooltip>
        </div>

        {BASE_MAP_LAYERS.map((layer) => {
          const isSelected = selectedLayers.includes(layer.value)

          return (
            <div className={clsx(cls.layer, isSelected && cls.selected)} key={layer.value}>
              <Tooltip content={t(`map.layers.${layer.value}`)}>
                <span>
                  <Button
                    size="small"
                    variation="text"
                    className={cls.btn}
                    color={isSelected ? "primary" : undefined}
                    onClick={() => handleLayerToggle(layer.value)}
                    iconProps={{ name: layer.icon }}
                  />
                </span>
              </Tooltip>
            </div>
          )
        })}

        <Tooltip content={t("toggle_layers")}>
          <span>
            <Button
              iconProps={{ name: "chevron-tiny-bold-left", size: "small" }}
              size="small"
              variation="solid"
              className={cls.toggle}
              onClick={() => {
                setCollapsed((currentValue) => !currentValue)
                setSearchBarActive(false)
              }}
            />
          </span>
        </Tooltip>
      </div>

      {isSearchBarActive && <ClientsSearchBar setSearchBarActive={setSearchBarActive} />}
    </div>
  )
}

const ClientsSearchBar = ({ setSearchBarActive }: { setSearchBarActive: (setSearchBarActive: boolean) => void }) => {
  const [searchValue, setSearchValue] = useState("")

  const dispatch = useAppDispatch()

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()

    dispatch(setClientsSearchValue(searchValue))
  }

  function handleClose() {
    dispatch(setClientsSearchValue(""))
    setSearchBarActive(false)
  }

  useEffect(() => {
    return () => {
      dispatch(setClientsSearchValue(""))
    }
  }, [dispatch])

  return (
    <form className={cls.searchBar} onSubmit={handleSubmit}>
      <Input
        onChange={(e) => setSearchValue(e.currentTarget.value)}
        defaultValue={searchValue}
        placeholder="Wyszukaj klientów"
        className={cls.input}
        autoFocus
      />

      <Button
        title="Zamknij"
        onClick={handleClose}
        iconProps={{ name: "close", size: "small" }}
        size="small"
        type="button"
      />
    </form>
  )
}

export function getSelectedClientLayers(selectedLayers: MapLayer[]) {
  return selectedLayers.filter((selectedLayer) =>
    CLIENT_MAP_LAYERS.find((clientLayer) => clientLayer === selectedLayer)
  ) as Array<ClientMapLayer>
}
